import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import {
    Button,
    Loader,
    SingleLineTextInput,
    Text,
    ValidationError
} from "dash-styled"
import { turbine } from "neql"
import { useNumberInput } from "../../../../../../../../Hooks/useNumberInput"
import { MetricChooser } from "../MetricChooser"
import { TrainingTestVariables } from "../../../../../../../../Hooks/useQualifierTestCreate"
import { useDispatch } from "react-redux"
import { dirtinessChanged } from "../../../../../../../../Actions/Client/InputRender/InputRenderActions"

export interface TestCardSettingsProps {
    appID: string
    qualifier: turbine.TrainingTest
    showSettings: boolean
    reload: () => void
    isPreparingCreation?: boolean
    createOrUpdateTest: (params: TrainingTestVariables) => Promise<boolean>
    isLoading: boolean
    maxQuestions?: number
}

export const TestCardSettings: FC<TestCardSettingsProps> = ({
    appID,
    qualifier,
    showSettings,
    reload,
    isPreparingCreation,
    createOrUpdateTest,
    isLoading,
    maxQuestions = Infinity
}) => {
    const dispatch = useDispatch()
    const numQuestionsInput = useNumberInput(qualifier.numQuestions, true, {
        type: "integer",
        max: maxQuestions,
        min: 1
    })
    const coolOffPeriodInput = useNumberInput(qualifier.coolOffMinutes, true, {
        type: "integer"
    })
    const cutoffValueInput = useNumberInput(
        qualifier.cutoffValue?.valueOf(),
        true
    )
    const [cutoffOperator, setCuttoffOperator] = useState(
        qualifier.cutoffOperator
    )

    const [selectedMetric, setSelectedMetric] = useState<
        string | null | undefined
    >(qualifier.cutoffMetricName)

    const formPrestine = useMemo(
        () =>
            isPreparingCreation !== true &&
            qualifier.cutoffOperator === cutoffOperator &&
            qualifier.cutoffValue === cutoffValueInput.value &&
            qualifier.numQuestions === numQuestionsInput.value &&
            qualifier.coolOffMinutes === coolOffPeriodInput.value,
        [
            coolOffPeriodInput.value,
            cutoffOperator,
            cutoffValueInput.value,
            isPreparingCreation,
            numQuestionsInput.value,
            qualifier.coolOffMinutes,
            qualifier.cutoffOperator,
            qualifier.cutoffValue,
            qualifier.numQuestions
        ]
    )

    useEffect(() => {
        dispatch(dirtinessChanged(!formPrestine))
    }, [dispatch, formPrestine])

    const handleSave = useCallback(async () => {
        if (selectedMetric !== null && selectedMetric !== undefined) {
            const success = await createOrUpdateTest({
                id: qualifier.id,
                cooloff: coolOffPeriodInput.value,
                cutoffValue: cutoffValueInput.value,
                cutoffOperator: cutoffOperator ?? undefined,
                testNumQuestions: numQuestionsInput.value,
                cutoffMetricName: selectedMetric,
                cutoffMetricDisplayName: selectedMetric
            })
            if (success) {
                reload()
                numQuestionsInput.resetValidity()
            }
        }
    }, [
        coolOffPeriodInput.value,
        createOrUpdateTest,
        cutoffOperator,
        cutoffValueInput.value,
        numQuestionsInput,
        qualifier.id,
        reload,
        selectedMetric
    ])

    return (
        <div
            className={`flex flex-col lg:ml-7% space-y-3 mt-3 ${
                showSettings ? "" : "hidden"
            }`}
        >
            <div className="space-y-3">
                <div className="flex items-center overflow-hidden space-x-1">
                    <Text className="my-0 flex-shrink-0" size="sm">
                        Each person must answer
                    </Text>
                    <SingleLineTextInput
                        size="sm"
                        className="w-18 flex-shrink-0"
                        testId={`qualifiers-test-number-questions-input`}
                        type="number"
                        transparentBg={true}
                        value={numQuestionsInput.value?.toString()}
                        onChange={numQuestionsInput.onChange}
                    />
                    <Text className="my-0 flex-shrink-0" size="sm">
                        questions
                    </Text>
                    {!numQuestionsInput.isValid && (
                        <ValidationError>
                            min 1 and max question pool size ({maxQuestions})
                        </ValidationError>
                    )}
                </div>
                <MetricChooser
                    title="Pass test when"
                    appID={appID}
                    cutoffMetric={selectedMetric}
                    cutoffOperator={cutoffOperator}
                    onCutoffOperatorChange={setCuttoffOperator}
                    onCutoffValueChange={cutoffValueInput.onChange}
                    cutoffValue={cutoffValueInput.value?.toString()}
                    onCutoffMetricChange={setSelectedMetric}
                />
                <div className="flex items-center overflow-hidden space-x-1">
                    <Text className="my-0 flex-shrink-0" size="sm">
                        People must wait
                    </Text>
                    <SingleLineTextInput
                        size="sm"
                        className="w-18 flex-shrink-0"
                        testId={`qualifiers-test-cool-off-input`}
                        type="number"
                        transparentBg={true}
                        value={coolOffPeriodInput.value?.toString()}
                        onChange={coolOffPeriodInput.onChange}
                        pattern="\d+"
                    />
                    <Text className="my-0 flex-shrink-0" size="sm">
                        minute(s) before attempting the qualifier another time
                    </Text>
                </div>
            </div>
            <>
                {isLoading ? (
                    <div>
                        <Loader size="xxxl" className="my-0.5 w-max-content" />
                    </div>
                ) : (
                    <Button
                        size="sm"
                        testId={`qualifiers-test-save-button`}
                        className="px-4 mt-3 w-max-content"
                        disabled={isLoading || formPrestine || !selectedMetric}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                )}
            </>
        </div>
    )
}
